import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Inbox, LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { useAuth } from '../state/AuthContext';
import { useActiveRole } from '../state/RoleContext';
import { useTheme, type Theme } from '../state/ThemeContext';
import { paths } from '../lib/paths';
import { deriveContactPreferences } from '../services/communicationConsentService';
import { updateDb } from '../services/store';

/**
 * /me - personal hub and profile settings for any signed-in user.
 * League HQ Settings stays league-admin-only; this page owns personal account
 * display details and device preferences.
 */
export function MePage() {
  const { googleEmail, canAdmin, authReady } = useAuth();
  const { profile } = useActiveRole();
  const { theme, setTheme } = useTheme();

  if (!authReady) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', padding: 24 }}>
        <p className="muted">Loading...</p>
      </div>
    );
  }

  const onProfile = (patch: Partial<typeof profile>) => {
    updateDb((db) => ({
      ...db,
      profiles: db.profiles.map((item) =>
        item.id === profile.id
          ? (() => {
              const updated = { ...item, ...patch, updatedAt: new Date().toISOString() };
              return {
                ...updated,
                contactPreferences: patch.phone !== undefined
                  ? deriveContactPreferences(updated, db.communicationConsents)
                  : updated.contactPreferences,
              };
            })()
          : item,
      ),
    }));
  };
  const onTheme = (next: Theme) => setTheme(next);

  return (
    <div style={{ maxWidth: 920, margin: '40px auto', padding: 24 }}>
      <div className="grid grid--2">
        <div className="card card--accent" style={{ padding: 24 }}>
          <div className="eyebrow">Your hub</div>
          <h1 style={{ marginTop: 6, fontSize: 28 }}>
            Hi{googleEmail ? `, ${googleEmail.split('@')[0]}` : ''}.
          </h1>
          <p className="muted" style={{ marginTop: 8 }}>
            This is your personal Scorekeeper space: inbox, event links, profile details, and display preferences.
          </p>

          <div className="stack" style={{ gap: 8, marginTop: 18 }}>
            <Link to={paths.meInbox} className="btn">
              <Inbox size={14} /> Open inbox
            </Link>
            <Link to={paths.events} className="btn">
              Browse events
            </Link>
            {canAdmin && (
              <Link to={paths.adminHome} className="btn btn--primary">
                Open League HQ
              </Link>
            )}
          </div>

          <div className="divider" />

          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 13 }}>
              {googleEmail ?? 'Not signed in'}
            </span>
            <a href="/api/auth/logout?return_to=/" className="btn btn--ghost btn--sm">
              <LogOut size={14} /> Sign out
            </a>
          </div>
        </div>

        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title"><UserRound size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Profile</h3>
              <p className="card__sub">These details connect your sign-in to your Directory profile and personal inbox.</p>
            </div>
          </div>
          <div className="stack">
            <Field label="Display name">
              <input className="input" value={profile.displayName} onChange={(event) => onProfile({ displayName: event.target.value })} />
            </Field>
            <Field label="Email">
              <input type="email" className="input" value={profile.email ?? ''} onChange={(event) => onProfile({ email: event.target.value })} />
            </Field>
            <Field label="Phone">
              <input className="input" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" value={profile.phone ?? ''} onChange={(event) => onProfile({ phone: event.target.value })} />
            </Field>
            <Field label="Avatar URL">
              <input className="input" value={profile.avatarUrl ?? ''} onChange={(event) => onProfile({ avatarUrl: event.target.value })} placeholder="https://..." />
            </Field>
            <Field label="Bio">
              <textarea className="input" rows={3} value={profile.bio ?? ''} onChange={(event) => onProfile({ bio: event.target.value })} />
            </Field>
            <Field label="Theme">
              <div role="radiogroup" className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={theme === 'light'}
                  onClick={() => onTheme('light')}
                  className={`btn btn--sm ${theme === 'light' ? 'btn--primary' : 'btn--ghost'}`}
                >
                  <Sun size={14} /> Light
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={theme === 'dark'}
                  onClick={() => onTheme('dark')}
                  className={`btn btn--sm ${theme === 'dark' ? 'btn--primary' : 'btn--ghost'}`}
                >
                  <Moon size={14} /> Dark
                </button>
              </div>
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      {children}
    </div>
  );
}
